import React from 'react';

import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';

import { LicenseInfo } from '@mui/x-license';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Yup from 'yup';
import { ptForm } from 'yup-locale-pt';

import { CustomSnackbarProvider } from 'components/snackbar';
import { ApiProvider } from 'context/ApiContext';
import { ApplicationProvider } from 'context/ApplicationContext';
import { AuthProvider } from 'context/AuthContext';
import { ClientProvider } from 'context/ClientContext';
import { LoaderProvider } from 'context/LoaderContext';
import { PermissionsProvider } from 'context/PermissionsContext';
import { router } from 'routes';
import { CustomTheme } from 'styles/customTheme';

if (typeof import.meta.env.VITE_MUI_LICENCE === 'string') {
    LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENCE);
}

Yup.setLocale(ptForm);

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: false,
        },
    },
});

const container = document.getElementById('app');
const root = createRoot(container as HTMLElement);

root.render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            <LoaderProvider>
                <ClientProvider>
                    <CustomTheme>
                        <AuthProvider>
                            <CustomSnackbarProvider>
                                <ApiProvider>
                                    <ApplicationProvider>
                                        <PermissionsProvider>
                                            <RouterProvider router={router} />
                                        </PermissionsProvider>
                                    </ApplicationProvider>
                                </ApiProvider>
                            </CustomSnackbarProvider>
                        </AuthProvider>
                    </CustomTheme>
                </ClientProvider>
            </LoaderProvider>
        </QueryClientProvider>
    </React.StrictMode>,
);
